import { ArrowClockwise } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SourceTrace } from "~/api/conversationTypes";
import { useSourceTraces } from "~/api/queries";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";

/**
 * The gather tools that have icons. `tool` is an OPEN set server-side, so an unrecognised one
 * gets the generic link glyph rather than nothing — a trace with no icon still has to read as
 * a trace.
 */
const TOOL_ICON: Record<string, string> = {
  search_and_extract: "🔎",
  crawl_url: "🌐",
  crawl_site: "🕸️",
  extract_pdf: "📄",
  extract_structured: "🧱",
  screenshot: "📸",
  execute_js: "⚙️",
};

const iconFor = (tool: string) => TOOL_ICON[tool] ?? "🔗";

/**
 * Where an answer's facts came from (W8 §B.6 / S7).
 *
 * This is the provenance surface for the whole DOCUMENT, not one message:
 * `GET /api/documents/{id}/source-traces`, newest first, hard-capped at 500 server-side. Every
 * time a thinker searched or crawled to answer, a row landed here — so a reader can tell an
 * answer that consulted the web from one that did not, which is the only way "the AI said so"
 * becomes checkable.
 */
export function SourcesPanel({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const traces = useSourceTraces(documentId);
  const [thinker, setThinker] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = traces.data ?? [];
  const thinkers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const trace of rows) {
      seen.set(trace.thinkerId, trace.thinkerName ?? trace.thinkerId);
    }
    return [...seen.entries()];
  }, [rows]);

  const visible = thinker ? rows.filter((trace) => trace.thinkerId === thinker) : rows;
  const sourceCount = visible.reduce((total, trace) => total + trace.sources.length, 0);

  return (
    <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
      <header className="flex items-center gap-2">
        <Text variant="small" className="min-w-0 flex-1 text-text-secondary">
          {t("{{steps}} gather steps · {{sources}} sources", {
            steps: visible.length,
            sources: sourceCount,
          })}
        </Text>
        <IconButton
          icon={ArrowClockwise}
          size="sm"
          aria-label={t("Refresh")}
          onClick={() => void traces.refetch()}
        />
      </header>

      {thinkers.length > 1 ? (
        <div className="flex flex-wrap gap-1" role="group" aria-label={t("Filter by thinker")}>
          <button
            type="button"
            aria-pressed={thinker === ""}
            onClick={() => setThinker("")}
            className={cn(
              "rounded-sm px-2 py-0.5 text-caption",
              thinker === ""
                ? "bg-accent text-accent-text"
                : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
            )}
          >
            {t("All thinkers")}
          </button>
          {thinkers.map(([id, name]) => (
            <button
              key={id}
              type="button"
              aria-pressed={thinker === id}
              onClick={() => setThinker(id)}
              className={cn(
                "rounded-sm px-2 py-0.5 text-caption",
                thinker === id
                  ? "bg-accent text-accent-text"
                  : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
              )}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}

      {traces.isLoading ? <Spinner label={t("Loading")} /> : null}

      {!traces.isLoading && visible.length === 0 ? (
        <Empty
          message={t(
            "No web sources yet. When a thinker searches or crawls the web to answer, every source it used will appear here.",
          )}
        />
      ) : null}

      <ul className="flex flex-col gap-1.5">
        {visible.map((trace) => (
          <li key={trace.id} className="rounded-md border border-divider p-2">
            <button
              type="button"
              aria-expanded={openId === trace.id}
              onClick={() => setOpenId(openId === trace.id ? null : trace.id)}
              className="flex w-full items-center gap-2 text-start"
            >
              <span aria-hidden>{iconFor(trace.tool)}</span>
              <span className="min-w-0 flex-1 truncate text-small text-text">
                {trace.query || trace.requestUrl || trace.tool}
              </span>
              {trace.status === "error" ? (
                <span className="rounded-sm bg-danger/10 px-1.5 py-0.5 text-caption text-danger">
                  {t("Failed")}
                </span>
              ) : (
                <span className="text-caption text-text-tertiary">
                  {t("{{count}} sources", { count: trace.sources.length })}
                </span>
              )}
            </button>
            {openId === trace.id ? <SourceList trace={trace} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceList({ trace }: { trace: SourceTrace }) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex flex-col gap-1 ps-6">
      <Text variant="small" className="text-text-tertiary">
        {trace.thinkerName ?? trace.thinkerId}
        {trace.engine ? ` · ${trace.engine}` : ""}
      </Text>
      {trace.sources.length === 0 ? (
        <Text variant="small" className="text-text-tertiary">
          {t("This step returned nothing.")}
        </Text>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {trace.sources.map((source) => (
            <li key={source.url} className="min-w-0 truncate">
              <a
                href={source.url}
                target="_blank"
                rel="noreferrer noopener"
                dir="ltr"
                className="text-small text-link hover:underline"
              >
                {source.title || source.url}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
